import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  identificationMenu,
  identificationTypes,
} from './identification_types';
import normalizeImage from './normalize_image';
import { compareFaces, registerProfile } from './api';

// アプリ版（IdentificationScreen.js〜Step4Screen.js）と同等のフローを Web 上で再現する。
// 5 ステップ構成: entry → guide → preview → selfie → profile

const ACADEMIC_YEARS_OPTIONS = [
  { title: '4年制', value: 4 },
  { title: '3年制', value: 3 },
  { title: '2年制', value: 2 },
  { title: '1年制', value: 1 },
];

const MAIN_COLOR = '#ff7680';
const WARNING_COLOR = '#d0021b';

const buildProceedUrl = (redirectUrl, fallbackUrl) => {
  if (!redirectUrl) {
    return fallbackUrl;
  }
  try {
    const url = new URL(redirectUrl, window.location.origin);
    // アプリ版踏襲: WebView 等で完了後にリロードさせるための適当なパラメータ
    url.searchParams.set('__rat', `${Date.now()}`);
    return url.toString();
  } catch (_) {
    return fallbackUrl;
  }
};

// 単純なモーダル（Bootstrap JS に依存しない）
const Modal = ({ isOpen, onClose, children, dismissable = true }) => {
  if (!isOpen) {
    return null;
  }
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: 16,
      }}
      onClick={dismissable ? onClose : undefined}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          maxWidth: 480,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  children: PropTypes.node,
  dismissable: PropTypes.bool,
};

const AlertModal = ({ alert, onDismiss }) => (
  <Modal isOpen={!!alert} onClose={onDismiss} dismissable={false}>
    {alert && (
      <div style={{ padding: 24 }}>
        {alert.title && (
          <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}>
            {alert.title}
          </div>
        )}
        {alert.message && (
          <div style={{ fontSize: 14, marginBottom: 24, whiteSpace: 'pre-wrap' }}>
            {alert.message}
          </div>
        )}
        <div style={{ textAlign: 'right' }}>
          <button
            type="button"
            className="btn"
            style={{ color: MAIN_COLOR, fontWeight: 'bold' }}
            onClick={onDismiss}
          >
            OK
          </button>
        </div>
      </div>
    )}
  </Modal>
);

AlertModal.propTypes = {
  alert: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
  }),
  onDismiss: PropTypes.func.isRequired,
};

const LoadingOverlay = ({ message }) => (
  <Modal isOpen dismissable={false}>
    <div style={{ padding: 32, textAlign: 'center' }}>
      <div
        className="spinner-border"
        role="status"
        style={{ color: MAIN_COLOR, marginBottom: 16 }}
      >
        <span className="sr-only">Loading...</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 'bold' }}>{message}</div>
    </div>
  </Modal>
);

LoadingOverlay.propTypes = {
  message: PropTypes.string.isRequired,
};

const PrimaryButton = ({ disabled, onClick, children, type = 'button', style }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      width: '100%',
      height: 44,
      borderRadius: 4,
      border: 'none',
      color: '#fff',
      fontWeight: 'bold',
      fontSize: 16,
      backgroundColor: disabled ? '#C0C0C0' : MAIN_COLOR,
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

PrimaryButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  type: PropTypes.string,
  style: PropTypes.object,
};

const OutlineButton = ({ onClick, children, style }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: '100%',
      height: 44,
      borderRadius: 4,
      border: `1px solid ${MAIN_COLOR}`,
      color: MAIN_COLOR,
      fontWeight: 'bold',
      fontSize: 16,
      backgroundColor: '#fff',
      cursor: 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

OutlineButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  style: PropTypes.object,
};

// ファイル input をプログラム的にトリガーするフック
// camera = 'environment' で背面カメラ, 'user' で前面カメラ
// camera = null/false なら capture 属性を付けず、カメラ/ライブラリ選択を可能にする (TGW-1723)
const useFileCapture = (camera, onPick) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) {
      onPick(file);
    }
  };

  const trigger = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, []);

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      capture={camera || undefined}
      onChange={handleChange}
      style={{ display: 'none' }}
    />
  );

  return { trigger, input };
};

const EntryStep = ({ onStart }) => (
  <div style={{ padding: '32px 24px', maxWidth: 560, margin: '0 auto' }}>
    <div
      style={{
        backgroundColor: '#F5F5F5',
        borderRadius: 3,
        padding: 36,
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: 20, fontWeight: 'bold', margin: 0 }}>自撮りで本人確認</h1>
      <p style={{ fontSize: 12, fontWeight: 'bold', marginTop: 24, marginBottom: 0 }}>
        ご自身の顔と本人確認書類を
        <br />
        お手持ちのスマホ・PCで撮影
      </p>
    </div>

    <div style={{ marginTop: 32, display: 'flex' }}>
      <span
        aria-hidden="true"
        style={{
          fontSize: 18,
          marginRight: 8,
          color: MAIN_COLOR,
        }}
      >
        🛡️
      </span>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 12, color: '#898989', margin: 0 }}>
          個人情報は本人確認にのみ利用され、安全に管理されます。本人確認で取得した個人情報が、他のユーザーに表示されることはありません。
        </p>
        <p style={{ fontSize: 16, fontWeight: 'bold', marginTop: 24, marginBottom: 12 }}>
          本人確認すると...
        </p>
        <p style={{ fontSize: 12, color: '#898989', margin: 0 }}>
          <strong style={{ color: '#222' }}>
            他のユーザーに本名を「非表示」に設定することができるようになります。
          </strong>
          <br />
          <br />
          ニックネームのみでやり取りすることができます。
        </p>
      </div>
    </div>

    <p style={{ marginTop: 24, fontSize: 11, color: '#222' }}>
      <strong>ご利用いただける本人確認書類：</strong>
      免許証 / マイナンバーカード / 在留カード / パスポート / 学生証
    </p>

    <div style={{ marginTop: 8 }}>
      <PrimaryButton onClick={onStart}>同意して撮影を開始する</PrimaryButton>
    </div>
  </div>
);

EntryStep.propTypes = {
  onStart: PropTypes.func.isRequired,
};

const DocumentPickerModal = ({ isOpen, onClose, onSelect }) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <div>
      <div
        style={{
          padding: 16,
          borderBottom: '1px solid #cdcdcd',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          style={{
            background: 'none',
            border: 'none',
            fontSize: 20,
            padding: 8,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        <h2 style={{ fontSize: 16, fontWeight: 'bold', margin: '0 0 0 8px' }}>
          本人確認書類の選択
        </h2>
      </div>
      <div style={{ padding: 32, borderBottom: '1px solid #cdcdcd' }}>
        <p style={{ fontSize: 14, fontWeight: 'bold', margin: 0 }}>自撮りで本人確認</p>
        <p style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          スマホ・PCのカメラでご自身の顔と本人確認書類を撮影します
        </p>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {identificationMenu.map((item, idx) => (
          <li
            key={item.type}
            style={{
              borderBottom: idx < identificationMenu.length - 1 ? '1px solid #cdcdcd' : 'none',
            }}
          >
            <button
              type="button"
              onClick={() => onSelect(item.type)}
              style={{
                width: '100%',
                padding: 24,
                background: 'none',
                border: 'none',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              <span style={{ flex: 1 }}>{item.title}</span>
              <span aria-hidden="true" style={{ color: 'rgba(0, 0, 0, 0.26)' }}>
                ›
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div style={{ padding: 16, borderTop: '1px solid #cdcdcd' }}>
        <p style={{ fontSize: 12, color: '#898989', margin: 0 }}>
          ※
          学生証認証は、今年度末（3月31日）時点で23歳以下の大学、短大、専門学校在学の方のみご利用いただけます。
        </p>
        <p style={{ fontSize: 12, color: '#898989', marginTop: 12, marginBottom: 0 }}>
          ※ 2020年2月以降に発行された新パスポートはご利用いただけません
        </p>
      </div>
    </div>
  </Modal>
);

DocumentPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const GuideStep = ({ type, rulesUrl, privacyPolicyUrl, onProceedCamera, onProceedLibrary, onBack }) => {
  const meta = identificationTypes[type];
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '70vh',
        maxWidth: 560,
        margin: '0 auto',
      }}
    >
      <div style={{ flex: 1, padding: '36px 16px' }}>
        <h2 style={{ textAlign: 'center', fontSize: 20, fontWeight: 'bold', margin: 0 }}>
          {meta.name}をご用意ください
        </h2>
        {meta.alert && (
          <div style={{ marginTop: 16, display: 'flex' }}>
            <span aria-hidden="true" style={{ color: WARNING_COLOR, marginRight: 4 }}>
              ⚠
            </span>
            <span style={{ fontSize: 12, flex: 1, marginTop: 2 }}>{meta.alert}</span>
          </div>
        )}
        <div style={{ position: 'relative', marginTop: 36 }}>
          <div
            style={{
              border: `2px solid ${MAIN_COLOR}`,
              borderRadius: 4,
              padding: '20px 16px 16px',
            }}
          >
            <p style={{ textAlign: 'center', fontSize: 16, fontWeight: 'bold', margin: 0 }}>
              お客さまの本人情報を守ります
            </p>
            <p style={{ marginTop: 12, fontSize: 14, marginBottom: 0 }}>
              お客さまからいただいた個人情報はつなげーとが大切に管理し、本人確認以外には使いません。
            </p>
          </div>
          <div
            style={{
              position: 'absolute',
              top: -15,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                backgroundColor: '#fff',
                color: MAIN_COLOR,
                fontSize: 18,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              🛡️
            </span>
          </div>
        </div>
      </div>
      <div style={{ borderTop: '1px solid rgba(0, 0, 0, 0.26)', padding: 16 }}>
        <p style={{ paddingLeft: 8, color: 'rgba(0, 0, 0, 0.54)', fontSize: 14, margin: 0 }}>
          <a href={rulesUrl} target="_blank" rel="noreferrer" style={{ color: '#48A3E9' }}>
            つなげーと利用規約
          </a>
          および
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: '#48A3E9' }}
          >
            プライバシーと情報セキュリティの方針
          </a>
          をご確認いただき、内容に同意した上で次へお進みください。
        </p>
        {/* 並列な2択（カメラ/フォルダ）を同格で縦に並べ、階層の違う「戻る」は
            テキストリンクとして最下段に置く */}
        <div style={{ marginTop: 16 }}>
          <PrimaryButton onClick={onProceedCamera}>同意してカメラで撮影</PrimaryButton>
        </div>
        <div style={{ marginTop: 8 }}>
          <OutlineButton onClick={onProceedLibrary}>同意してフォルダから選択</OutlineButton>
        </div>
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: '#48A3E9',
              fontSize: 14,
              fontWeight: 'bold',
              padding: 8,
              cursor: 'pointer',
            }}
          >
            戻る
          </button>
        </div>
      </div>
    </div>
  );
};

GuideStep.propTypes = {
  type: PropTypes.string.isRequired,
  rulesUrl: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string.isRequired,
  onProceedCamera: PropTypes.func.isRequired,
  onProceedLibrary: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

const PreviewStep = ({ type, frontImage, backImage, onRetake, onProceed }) => {
  const meta = identificationTypes[type];
  const frontUrl = useMemo(() => (frontImage ? URL.createObjectURL(frontImage) : null), [frontImage]);
  const backUrl = useMemo(() => (backImage ? URL.createObjectURL(backImage) : null), [backImage]);

  useEffect(() => () => {
    if (frontUrl) URL.revokeObjectURL(frontUrl);
    if (backUrl) URL.revokeObjectURL(backUrl);
  }, [frontUrl, backUrl]);

  return (
    <div style={{ paddingBottom: 96, maxWidth: 560, margin: '0 auto' }}>
      <div style={{ padding: 16, textAlign: 'center' }}>
        <p style={{ fontSize: 18, fontWeight: 'bold', marginTop: 16 }}>{meta.frontLabel}</p>
        {frontUrl && (
          <img
            src={frontUrl}
            alt={meta.frontLabel}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        )}
        {meta.useBack && (
          <>
            <p style={{ fontSize: 18, fontWeight: 'bold', marginTop: 24 }}>{meta.backLabel}</p>
            {backUrl && (
              <img
                src={backUrl}
                alt={meta.backLabel}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )}
          </>
        )}
      </div>
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 16,
          backgroundColor: '#fff',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          gap: 8,
          maxWidth: 560,
          margin: '0 auto',
        }}
      >
        <div style={{ flex: 1 }}>
          <OutlineButton onClick={onRetake}>再撮影</OutlineButton>
        </div>
        <div style={{ flex: 1 }}>
          <PrimaryButton onClick={onProceed}>確認して次へ</PrimaryButton>
        </div>
      </div>
    </div>
  );
};

PreviewStep.propTypes = {
  type: PropTypes.string.isRequired,
  frontImage: PropTypes.object.isRequired,
  backImage: PropTypes.object,
  onRetake: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
};

const FormRow = ({ label, children }) => (
  <div
    style={{
      display: 'flex',
      backgroundColor: '#fff',
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      alignItems: 'center',
    }}
  >
    <label style={{ fontSize: 16, fontWeight: 'bold', padding: 16, margin: 0, minWidth: 130 }}>
      {label}
    </label>
    <div style={{ flex: 1, padding: '8px 16px' }}>{children}</div>
  </div>
);

FormRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const ProfileStep = ({ type, isSubmitting, onSubmit }) => {
  const isStudent = type === 'studentCard';

  const [familyName, setFamilyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [familyNameKana, setFamilyNameKana] = useState('');
  const [firstNameKana, setFirstNameKana] = useState('');
  const [isFullNameHidden, setIsFullNameHidden] = useState(false);

  const [schoolName, setSchoolName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [enrolledAt, setEnrolledAt] = useState('');
  const [academicYears, setAcademicYears] = useState(4);

  const isValidProfile = familyName && firstName && familyNameKana && firstNameKana;
  const isValidStudent = !isStudent || (schoolName && birthdate && enrolledAt && academicYears);
  const isValid = isValidProfile && isValidStudent && !isSubmitting;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }
    onSubmit({
      familyName,
      firstName,
      familyNameKana,
      firstNameKana,
      isFullNameHidden,
      student: isStudent
        ? {
            schoolName,
            // バックエンドの想定 (アプリ版踏襲): 'YYYY-MM-DD HH:mm:ss' (Asia/Tokyo)
            birthdate: `${birthdate} 00:00:00`,
            enrolledAt: `${enrolledAt} 00:00:00`,
            academicYears,
          }
        : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ paddingBottom: 32, maxWidth: 560, margin: '0 auto' }}>
      <div style={{ backgroundColor: '#F5F5F5', padding: 18, margin: 16 }}>
        <p style={{ color: WARNING_COLOR, fontSize: 16, fontWeight: 'bold', margin: 0 }}>
          撮影した本人確認書類の記載と同じ表記で入力してください
        </p>
      </div>
      <div style={{ margin: '0 16px' }}>
        <p style={{ fontSize: 12, margin: 0 }}>
          ※本人確認書類の表記がローマ字の場合は、ローマ字で姓名を入力してください。
        </p>
        <p style={{ fontSize: 12, margin: 0 }}>
          ※本人確認書類の表記が新姓と旧姓で併記されている場合は、新姓のみを入力してください。
        </p>
      </div>

      <div style={{ marginTop: 16 }}>
        <FormRow label="姓（全角）">
          <input
            type="text"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            placeholder="例）山田"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
        <FormRow label="名（全角）">
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="例）花子"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
        <FormRow label="姓カナ（全角）">
          <input
            type="text"
            value={familyNameKana}
            onChange={(e) => setFamilyNameKana(e.target.value)}
            placeholder="例）ヤマダ"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
        <FormRow label="名カナ（全角）">
          <input
            type="text"
            value={firstNameKana}
            onChange={(e) => setFirstNameKana(e.target.value)}
            placeholder="例）ハナコ"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
      </div>

      {isStudent && (
        <>
          <div style={{ padding: '24px 16px 8px' }}>
            <p style={{ fontWeight: 'bold', margin: 0 }}>学生情報</p>
          </div>
          <FormRow label="学校名">
            <input
              type="text"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              placeholder="つなげーと大学"
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            />
          </FormRow>
          <FormRow label="生年月日">
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            />
          </FormRow>
          <FormRow label="入学年月日">
            <input
              type="date"
              value={enrolledAt}
              onChange={(e) => setEnrolledAt(e.target.value)}
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            />
          </FormRow>
          <FormRow label="学業年数">
            <select
              value={academicYears}
              onChange={(e) => setAcademicYears(Number(e.target.value))}
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            >
              {ACADEMIC_YEARS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.title}
                </option>
              ))}
            </select>
          </FormRow>
        </>
      )}

      <div style={{ padding: '24px 24px' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
          <input
            type="checkbox"
            checked={isFullNameHidden}
            onChange={(e) => setIsFullNameHidden(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <span>氏名を他のユーザーに表示しない</span>
        </label>
      </div>

      <div style={{ padding: '0 16px 24px' }}>
        <PrimaryButton type="submit" disabled={!isValid}>
          {isSubmitting ? '送信中...' : '次へ'}
        </PrimaryButton>
      </div>
    </form>
  );
};

ProfileStep.propTypes = {
  type: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const IdentificationWizard = ({
  redirectUrl,
  rulesUrl,
  privacyPolicyUrl,
  homeUrl,
}) => {
  // フロー状態
  const [step, setStep] = useState('entry'); // entry|guide|preview|selfie|profile
  const [isPickingDocument, setIsPickingDocument] = useState(false);
  const [type, setType] = useState(null);
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);

  // ステップ間の通知用 alert
  const [alert, setAlert] = useState(null);
  const [pendingAfterAlert, setPendingAfterAlert] = useState(null);

  // フェーズフラグ
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isVerifyingFace, setIsVerifyingFace] = useState(false);
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);

  const isSubmittingProfileRef = useRef(false);

  // ファイル input トリガー
  const onFrontPicked = useCallback(async (file) => {
    setIsProcessingImage(true);
    try {
      const normalized = await normalizeImage(file, 'front.jpg');
      setFrontImage(normalized);
      const meta = identificationTypes[type];
      if (meta && meta.useBack) {
        // 続けて裏面撮影をトリガー（次の useEffect で起動するためのフラグは持たず、
        // ボタン押下フローで戻ってくる方式にする）
        setStep('guide-back');
      } else {
        setStep('preview');
      }
    } catch (e) {
      setAlert({ title: '画像の読み込みに失敗しました', message: '別の画像で再度お試しください。' });
    } finally {
      setIsProcessingImage(false);
    }
  }, [type]);

  const onBackPicked = useCallback(async (file) => {
    setIsProcessingImage(true);
    try {
      const normalized = await normalizeImage(file, 'back.jpg');
      setBackImage(normalized);
      setStep('preview');
    } catch (e) {
      setAlert({ title: '画像の読み込みに失敗しました', message: '別の画像で再度お試しください。' });
    } finally {
      setIsProcessingImage(false);
    }
  }, []);

  const onSelfiePicked = useCallback(async (file) => {
    setIsProcessingImage(true);
    let selfie = null;
    try {
      selfie = await normalizeImage(file, 'selfie.jpg');
    } catch (e) {
      setIsProcessingImage(false);
      setAlert({ title: '画像の読み込みに失敗しました', message: '別の画像で再度お試しください。' });
      return;
    }
    setIsProcessingImage(false);

    setIsVerifyingFace(true);
    try {
      const response = await compareFaces({
        frontImage,
        backImage,
        selfieImage: selfie,
      });
      setIsVerifyingFace(false);

      if (response.alert) {
        const next = response.success ? () => setStep('profile') : null;
        setAlert(response.alert);
        setPendingAfterAlert(() => next);
      } else if (response.success) {
        setStep('profile');
      } else {
        setAlert({
          title: '本人確認ができません',
          message: '再度、カメラでお顔の撮影を行ってください。',
        });
      }
    } catch (e) {
      setIsVerifyingFace(false);
      setAlert({
        title: '本人確認ができません',
        message: '再度、カメラでお顔の撮影を行ってください。',
      });
    }
  }, [frontImage, backImage]);

  // ファイル input
  // 身分証はカメラ撮影とフォルダ選択の両方を許可し、ボタンで出し分ける。
  // セルフィーはカメラ限定を維持(偽装対策)。capture はモバイルのみ有効で、PCではファイル選択になる
  const frontCamera = useFileCapture('environment', onFrontPicked);
  const frontLibrary = useFileCapture(null, onFrontPicked);
  const backCamera = useFileCapture('environment', onBackPicked);
  const backLibrary = useFileCapture(null, onBackPicked);
  const selfie = useFileCapture('user', onSelfiePicked);

  const startFlow = useCallback(() => {
    setIsPickingDocument(true);
  }, []);

  const handleDocumentSelect = useCallback((selectedType) => {
    setType(selectedType);
    setIsPickingDocument(false);
    setFrontImage(null);
    setBackImage(null);
    setStep('guide');
  }, []);

  const handleAlertDismiss = useCallback(() => {
    const cb = pendingAfterAlert;
    setAlert(null);
    setPendingAfterAlert(null);
    if (typeof cb === 'function') {
      cb();
    }
  }, [pendingAfterAlert]);

  const handleProfileSubmit = useCallback(async (values) => {
    if (isSubmittingProfileRef.current) {
      return;
    }
    isSubmittingProfileRef.current = true;
    setIsSubmittingProfile(true);

    try {
      const response = await registerProfile(values);
      const success = response.success;

      const proceedOnSuccess = () => {
        const dest = buildProceedUrl(redirectUrl, homeUrl);
        window.location.assign(dest);
      };

      if (response.alert) {
        if (success) {
          setAlert(response.alert);
          setPendingAfterAlert(() => proceedOnSuccess);
        } else {
          setAlert(response.alert);
          isSubmittingProfileRef.current = false;
          setIsSubmittingProfile(false);
        }
      } else if (success) {
        proceedOnSuccess();
      } else {
        setAlert({
          title: '本人情報の登録に失敗しました',
          message: 'しばらく時間をおいてから再度お試しください。',
        });
        isSubmittingProfileRef.current = false;
        setIsSubmittingProfile(false);
      }
    } catch (e) {
      setAlert({
        title: '本人情報の登録に失敗しました',
        message: 'しばらく時間をおいてから再度お試しください。',
      });
      isSubmittingProfileRef.current = false;
      setIsSubmittingProfile(false);
    }
  }, [redirectUrl, homeUrl]);

  // ステップ別のレンダリング
  let body = null;
  if (step === 'entry') {
    body = (
      <EntryStep onStart={startFlow} />
    );
  } else if (step === 'guide') {
    body = (
      <GuideStep
        type={type}
        rulesUrl={rulesUrl}
        privacyPolicyUrl={privacyPolicyUrl}
        onProceedCamera={() => frontCamera.trigger()}
        onProceedLibrary={() => frontLibrary.trigger()}
        onBack={() => {
          setType(null);
          setStep('entry');
        }}
      />
    );
  } else if (step === 'guide-back') {
    // 裏面撮影の案内 + 起動
    body = (
      <div style={{ padding: 36, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <h2 style={{ fontSize: 18, fontWeight: 'bold' }}>
          {identificationTypes[type].name}の{identificationTypes[type].backLabel}を撮影してください
        </h2>
        <div style={{ marginTop: 24 }}>
          <PrimaryButton onClick={() => backCamera.trigger()}>裏面をカメラで撮影</PrimaryButton>
        </div>
        <div style={{ marginTop: 12 }}>
          <OutlineButton onClick={() => backLibrary.trigger()}>フォルダから選択</OutlineButton>
        </div>
        <div style={{ marginTop: 12 }}>
          <OutlineButton
            onClick={() => {
              setFrontImage(null);
              setStep('guide');
            }}
          >
            表面から撮り直す
          </OutlineButton>
        </div>
      </div>
    );
  } else if (step === 'preview') {
    body = (
      <PreviewStep
        type={type}
        frontImage={frontImage}
        backImage={backImage}
        onRetake={() => {
          setFrontImage(null);
          setBackImage(null);
          setStep('guide');
        }}
        onProceed={() => setStep('selfie')}
      />
    );
  } else if (step === 'selfie') {
    body = (
      <div style={{ padding: 36, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <h2 style={{ fontSize: 18, fontWeight: 'bold' }}>カメラでご自身を撮影しましょう</h2>
        <p style={{ marginTop: 16, fontSize: 14 }}>お顔全体が映るように調整してください。</p>
        <div style={{ marginTop: 24 }}>
          <PrimaryButton onClick={() => selfie.trigger()}>自撮りを撮影する</PrimaryButton>
        </div>
        <div style={{ marginTop: 12 }}>
          <OutlineButton onClick={() => setStep('preview')}>戻る</OutlineButton>
        </div>
      </div>
    );
  } else if (step === 'profile') {
    body = (
      <ProfileStep
        type={type}
        isSubmitting={isSubmittingProfile}
        onSubmit={handleProfileSubmit}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      {body}
      {frontCamera.input}
      {frontLibrary.input}
      {backCamera.input}
      {backLibrary.input}
      {selfie.input}
      <DocumentPickerModal
        isOpen={isPickingDocument}
        onClose={() => setIsPickingDocument(false)}
        onSelect={handleDocumentSelect}
      />
      {isProcessingImage && <LoadingOverlay message="画像を準備しています..." />}
      {isVerifyingFace && <LoadingOverlay message="確認しています..." />}
      {isSubmittingProfile && !alert && <LoadingOverlay message="送信しています..." />}
      <AlertModal alert={alert} onDismiss={handleAlertDismiss} />
    </div>
  );
};

IdentificationWizard.propTypes = {
  redirectUrl: PropTypes.string,
  rulesUrl: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string.isRequired,
  homeUrl: PropTypes.string.isRequired,
};

IdentificationWizard.defaultProps = {
  redirectUrl: null,
};

export default IdentificationWizard;
